import { useCallback, useEffect, useState } from "react";
import { ApprovalConfig, ApprovalConfigSchema } from "../models/approval";
import { toast } from "sonner";
import { fetchFastApi } from "../api/fastapi";

export const useApprovalConfig = ({
  fetchOnLoad = true,
}: {
  fetchOnLoad?: boolean;
}) => {
  const [loading, setLoading] = useState(fetchOnLoad);
  const [configs, setConfigs] = useState<ApprovalConfig[]>([]);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFastApi({
        path: "/approvals/config",
        method: "GET",
      });
      if (data.configs) {
        const parsed = ApprovalConfigSchema.array().parse(data.configs);
        setConfigs(parsed);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch approval configs");
    } finally {
      setLoading(false);
    }
  }, []);

  const createConfig = useCallback(
    async (body: {
      entityType: string;
      changeType: string;
      steps: {
        stepOrder: number;
        approverRoles: string[];
        requiredCount: number;
      }[];
    }) => {
      try {
        const data = await fetchFastApi({
          path: "/approvals/config",
          method: "POST",
          body,
        });
        await fetchConfigs();
        return data;
      } catch (error) {
        console.error(error);
        toast.error("Failed to create approval config");
      }
    },
    [fetchConfigs],
  );

  const updateConfig = useCallback(
    async (configId: string, body: { isEnabled: boolean }) => {
      try {
        await fetchFastApi({
          path: `/approvals/config/${configId}`,
          method: "PUT",
          body,
        });
        await fetchConfigs();
      } catch (error) {
        console.error(error);
        toast.error("Failed to update approval config");
      }
    },
    [fetchConfigs],
  );

  const deleteConfig = useCallback(
    async (configId: string) => {
      try {
        await fetchFastApi({
          path: `/approvals/config/${configId}`,
          method: "DELETE",
        });
        await fetchConfigs();
        toast.success("Approval config deleted");
      } catch (error) {
        console.error(error);
        toast.error("Failed to delete approval config");
      }
    },
    [fetchConfigs],
  );

  useEffect(() => {
    if (fetchOnLoad) {
      fetchConfigs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOnLoad]);

  return {
    loading,
    configs,
    fetchConfigs,
    createConfig,
    updateConfig,
    deleteConfig,
  };
};
